/**
 * Formatação de DURAÇÕES (tempo decorrido), compartilhada entre as telas de
 * monitoramento e de relatórios.
 *
 * Regra do produto: a contagem NÃO passa de 24 horas no campo de horas. A partir
 * de 24h a duração ganha um prefixo de dias — `58:17:33` virou `2D 10:17:33`.
 * Antes disso cada tela tinha a sua própria cópia da conta (Mapa de Ocupação,
 * Painel Sinótico e Linha do Tempo), e as horas cresciam indefinidamente.
 *
 * ATENÇÃO — o que NÃO pertence aqui:
 * - Relógios de parede (hora do dia): `formatClock`/`formatIsoTime` no Mapa de
 *   Ocupação, `formatLocalTime` em `workSchedule.ts`, `shiftWindowLabel`.
 * - Agregados em horas/minutos decimais que dividem eixo com um gráfico:
 *   `fmtHours` do Pareto por Estado ("312,4 h") e os minutos do Tempo de
 *   Preparação ("38,9 min"). Ali o prefixo de dias quebraria a comparação com o
 *   eixo do gráfico.
 */

const SECONDS_PER_DAY = 86_400;
const SECONDS_PER_HOUR = 3_600;

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Quebra um total de segundos em dias/horas/minutos/segundos já normalizados. */
const split = (
  totalSeconds: number,
): { days: number; hours: number; minutes: number; seconds: number } => {
  const total = Math.max(0, Math.floor(totalSeconds));
  const rest = total % SECONDS_PER_DAY;
  return {
    days: Math.floor(total / SECONDS_PER_DAY),
    hours: Math.floor(rest / SECONDS_PER_HOUR),
    minutes: Math.floor((rest % SECONDS_PER_HOUR) / 60),
    seconds: rest % 60,
  };
};

export interface ElapsedClockOptions {
  /**
   * Esconde os segundos QUANDO há prefixo de dias ("2D 10:17" em vez de
   * "2D 10:17:33"). Usado só nos cards estreitos do mapa compacto, onde o texto
   * completo estouraria a largura e cairia no ellipsis.
   */
  compact?: boolean;
}

/**
 * Duração como relógio digital, com rollover de dias a partir de 24h.
 *
 * ```
 * formatElapsedClock(0)      // "00:00:00"
 * formatElapsedClock(3661)   // "01:01:01"
 * formatElapsedClock(86_400) // "1D 00:00:00"
 * formatElapsedClock(209_853)              // "2D 10:17:33"
 * formatElapsedClock(209_853, { compact: true }) // "2D 10:17"
 * ```
 */
export const formatElapsedClock = (
  totalSeconds: number,
  options?: ElapsedClockOptions,
): string => {
  const { days, hours, minutes, seconds } = split(totalSeconds);
  const clock = `${pad2(hours)}:${pad2(minutes)}`;
  if (days === 0) return `${clock}:${pad2(seconds)}`;
  // Abaixo de 24h o formato não muda — só o que passa de um dia ganha o "D".
  return options?.compact ? `${days}D ${clock}` : `${days}D ${clock}:${pad2(seconds)}`;
};

/**
 * Duração com unidades explícitas, também com rollover de dias a partir de 24h.
 * Mantém o comportamento histórico da Linha do Tempo: os segundos só aparecem
 * quando a duração é menor que um minuto.
 *
 * ```
 * formatDurationUnits(42)      // "42s"
 * formatDurationUnits(1_020)   // "17m"
 * formatDurationUnits(209_853) // "2D 10h 17m"
 * ```
 */
export const formatDurationUnits = (totalSeconds: number): string => {
  const { days, hours, minutes, seconds } = split(totalSeconds);
  if (days > 0) return `${days}D ${hours}h ${pad2(minutes)}m`;
  if (hours > 0) return `${hours}h ${pad2(minutes)}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
};
